import { useEffect, useRef, useState } from "react";
import { AgGridReact, CustomCellRendererProps } from "ag-grid-react";
import dayjs from "dayjs";
import {
  ColDef,
  FilterChangedEvent,
  GridReadyEvent,
  IGetRowsParams,
} from "ag-grid-community";
import { getFromCache, saveToCache } from "../../../utils/setAndGetFromStorage";
import MuiButton from "@mui/material/Button";
import { Button } from "../../../components/Button";
import { useNavigate } from "react-router-dom";
import { themeGrid } from "../../../themeGrid";
import { localeText } from "../../../localeTextFromGrid";
import { IHoliday, IHolidayFilter } from "../../../@types/holiday.type";
import { IGrid } from "../../../@types/grid.type";
import { searchHolidays } from "../../../services/api/holidays/holidaysService";
import vectorIcon from "../../../assets/_VectorIcon_.svg";
import addCircleImage from "./../../../assets/_AddCircleIcon_.svg";
import styles from "./styles.module.scss";
import usePermission from "../../../hooks/usePermission";
import { FeatureStatic } from "../../../@types/statics/feature.static";

export const Holidays: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const gridRef = useRef<AgGridReact<any>>(null);

  const navigate = useNavigate();
  const permissions = usePermission(FeatureStatic.holidays.id);

  const columns: ColDef[] = [
    {
      field: "name",
      headerName: "Nome",
      minWidth: 300,
      flex: 1,
    },
    {
      field: "holidayDate",
      headerName: "Data do feriado",
      minWidth: 300,
      flex: 1,
      valueGetter: (params) => params.data?.holidayDate,
      cellRenderer: (params: CustomCellRendererProps) => {
        return dayjs(params.value).format("DD/MM/YYYY");
      },
      filter: "agDateColumnFilter",
      filterParams: {
        comparator: (filterLocalDateAtMidnight: any, cellValue: any) => {
          const cellDate = dayjs(cellValue).startOf("day").toDate();
          if (cellDate < filterLocalDateAtMidnight) return -1;
          if (cellDate > filterLocalDateAtMidnight) return 1;
          return 0;
        },
      },
    },
    {
      field: "recurrent",
      headerName: "Recorrência (anual)",
      minWidth: 300,
      flex: 1,
      cellRenderer: (params: CustomCellRendererProps) => (
        <span>{params.value ? "Sim" : "Não"}</span>
      ),
    },
    {
      field: "holidayTypeName",
      headerName: "Tipo do feriado",
      minWidth: 300,
      flex: 1,
    },
  ];

  const [cachedFilters, setCachedFilters] = useState(
    getFromCache("agGridFiltersHolidays")
  );

  const [cachedSort, setCachedSort] = useState(
    getFromCache("agGridSortHolidays")
  );

  const onFilterChanged = (params: FilterChangedEvent) => {
    const filters = params.api.getFilterModel(); // Obtém o modelo de filtro
    saveToCache("agGridFiltersHolidays", filters); // Salva no cache
  };

  const onSortChanged = () => {
    const colState = gridRef.current!.api.getColumnState();
    const sortState = colState
      .filter(function (s) {
        return s.sort != null;
      })
      .map(function (s) {
        return { colId: s.colId, sort: s.sort, sortIndex: s.sortIndex };
      });

    saveToCache("agGridSortHolidays", sortState);
  };

  useEffect(() => {
    if (!permissions.canView()) {
      navigate("/unauthorized");
      return;
    }
  }, []);

  const onGridReady = (params: GridReadyEvent) => {
    const gridApi = params.api;

    // Restaurar filtros
    if (cachedFilters) {
      gridApi.setFilterModel(cachedFilters);
    }

    // Restaurar ordenação
    if (cachedSort) {
      gridApi.applyColumnState({
        state: cachedSort,
        defaultState: { sort: null },
      });
    }
  };

  const getDataSource = () => ({
    getRows: async (params: IGetRowsParams) => {
      const sortModel = params.sortModel;
      const filterModel = params.filterModel;

      const filter = {
        skip: params.startRow, // Linha inicial para paginação
        take: params.endRow - params.startRow, // Quantidade de linhas
      } as IHolidayFilter;

      if (sortModel.length > 0) {
        filter.orderByField = sortModel[0].colId;
        filter.orderByDirection = sortModel[0].sort == "asc" ? 0 : 1;
      }

      if (filterModel.name) {
        filter.name = filterModel.name.filter;
      }

      if (filterModel.holidayDate) {
        filter.holidayDate = dayjs(
          filterModel.holidayDate.dateFrom
        ).toISOString();
      }

      if (filterModel.recurrent) {
        filter.recurrent =
          String(filterModel.recurrent.filter).toLowerCase() == "sim";
      }

      if (filterModel.holidayTypeName) {
        filter.holidayTypeName = filterModel.holidayTypeName.filter;
      }

      try {
        const response = (await searchHolidays(filter)) as IGrid<IHoliday>;

        params.successCallback(response.items, response.totalRows);
      } catch (error) {
        console.error("Erro ao buscar dados:", error);
        params.failCallback(); // Notifica falha no carregamento
      } finally {
        setLoading(false);
      }
    },
  });

  function navigateToUpdatePage(id: string) {
    navigate(`update/${id}`);
  }

  return (
    <div className={styles.holidays}>
      <div className={styles.back}>
        <MuiButton
          className={styles.buttonMui}
          onClick={() => navigate("/configurations")}
        >
          {vectorIcon && <img src={vectorIcon} />}
          Voltar a tela anterior
        </MuiButton>
      </div>
      <div className={styles.titleAndBtnCreate}>
        <h1>Feriados</h1>

        <div className={styles.btnsFilterAndCreate}>
          <Button
            iconUrl={addCircleImage}
            onClick={() => {
              navigate("/configurations/holidays/create");
            }}
            content="Novo Feriado"
            className={styles.button}
          />
        </div>
      </div>

      <div className={`${styles.dataGridContainer}`}>
        <AgGridReact
          ref={gridRef}
          columnDefs={columns}
          theme={themeGrid}
          localeText={localeText}
          loading={loading}
          overlayNoRowsTemplate="<span style='color: gray;'>Sem registros encontrados</span>"
          defaultColDef={{
            sortable: true,
            editable: false,
            filter: true,
            floatingFilter: true,
          }}
          suppressCellFocus={true}
          rowStyle={{ cursor: "pointer" }}
          onRowClicked={(row) => navigateToUpdatePage(row.data.id)}
          cacheBlockSize={10}
          onFilterChanged={onFilterChanged}
          onSortChanged={onSortChanged}
          rowModelType="infinite"
          datasource={getDataSource()}
          animateRows={true}
          onGridReady={onGridReady}
        />
      </div>
    </div>
  );
};
